import { Autocomplete, Button, Grid, TextField, Tooltip, Select, MenuItem, Checkbox, FormControlLabel, AutocompleteChangeDetails } from '@mui/material';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import TonnageNameSelector from './components/TonnageNameSelector';
import ContactSelector from '../ContactSelector/ContactSelector';
import PortSelector from '../PortSelector/PortSelector';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import PortModel from '../PortSelector/models/PortModel';
import { addTonnageName, resetTonnageInput, setBeam, setCapacity, setClass, setDateOpenEnd, setDateOpenStart, setDeck, setDraft, setDwt, setFullTc, setGear, setGearless, setGrabs, setHeadOwner, setLoa, setOperator, setPortOpen, setRemarks, setSelectedTonnageId, setTonnageAdvanceInputExpand, setTonnageInput, setTonnageInputId, setTonnageQuickInputExpand, setTriggerFocusTonnageNameInput, setTriggerSave, setUpdateFullTc, setVesselName, setYearBuilt, TonnageInputModel, tonnageInputSelector } from './reducers/TonnageInputReducer';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import Utility from '../../app/utility';
import TonnageApi, { GetTonnageByIdParam, SaveTonnageParam } from './api/TonnageApi';
import BaseResponse from '../../app/BaseResponse';
import { setModalOpen, setTonnageHistories, setTonnageId } from './reducers/TonnageOperatorHistoryReducer';
import TonnageOperatorHistoryModal from './components/TonnageOperatorHistoryModal';
import TonnageModel from './models/TonnageModel';
import TonnageNameModel from './models/TonnageNameModel';
import Swal from 'sweetalert2';
import NumberFormatCustom from '../NumberFormatCustom/NumberFormatCustom';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import ContactNameModel from '../ContactSelector/models/ContactNameModel';
import { contactSelectorSelector, setContactSearchResults } from '../ContactSelector/reducers/ContactSelectorReducer';
import moment from 'moment';
import { setTriggerMatchingAreaModel } from './reducers/TonnageSearchReducer';
import { MatchingType } from '../MatchingPreview/MatchingConstants';
import TriggerMatchingAreaModel from '../MatchingPreview/models/TriggerMatchingAreaModel';

const TonnageInput: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const tonnageInputViewData = useSelector(tonnageInputSelector);
    let portSelectorData = useSelector(portSelectorSelector);
    const contactSelectorData = useSelector(contactSelectorSelector);

    useEffect(() => {
        if (tonnageInputViewData.triggerSave > 0) {
            saveTonnage(false);
            dispatch(setTriggerSave(0));
        }
    }, [tonnageInputViewData.triggerSave]);

    useEffect(() => {
        if (tonnageInputViewData.triggerRefreshInput > 0) {
            refreshInput();
        }
    }, [tonnageInputViewData.triggerRefreshInput]);

    const refreshInput = () => {
        dispatch(resetTonnageInput());
        dispatch(setSelectedTonnageId(""));
        dispatch(setTriggerFocusTonnageNameInput((new Date()).getTime()));
    }

    useEffect(() => {
        if (tonnageInputViewData.selectedTonnageId.length > 0) {
            selectTonnageById(tonnageInputViewData.selectedTonnageId);
        }
    }, [tonnageInputViewData.selectedTonnageId])

    const selectTonnageById = (id: string) => {
        dispatch(showLoading(true));
        TonnageApi.GetById({
            id: id
        }).then((response: BaseResponse<TonnageModel>) => {
            if (response.success) {
                let tonnageInput = Utility.CloneObject(tonnageInputViewData.tonnageInput);

                tonnageInput.id = response.data.id;
                tonnageInput.headOwner = response.data.headOwner ? response.data.headOwner : "";
                tonnageInput.operator = response.data.operator ? response.data.operator : "";
                tonnageInput.vesselName = response.data.vesselName;
                tonnageInput.dwt = response.data.dwt ? response.data.dwt : null;
                tonnageInput.yearBuilt = response.data.yearBuilt ? response.data.yearBuilt : null;
                tonnageInput.portOpen = response.data.portOpen;
                tonnageInput.dateOpenStart = Utility.DateFormat.format(new Date(response.data.dateOpenStart));
                tonnageInput.dateOpenEnd = Utility.DateFormat.format(new Date(response.data.dateOpenEnd));
                tonnageInput.remarks = response.data.remarks ? response.data.remarks : "";
                tonnageInput.gear = response.data.gear ? response.data.gear : null;
                tonnageInput.draft = response.data.draft ? response.data.draft : null;
                tonnageInput.loa = response.data.loa ? response.data.loa : null;
                tonnageInput.class = response.data.class ? response.data.class : "";
                tonnageInput.beam = response.data.beam ? response.data.beam : null;
                tonnageInput.grabs = response.data.grabs;
                tonnageInput.gearless = response.data.gearless;
                tonnageInput.deck = response.data.deck ? response.data.deck : "";
                tonnageInput.capacity = response.data.capacity ? response.data.capacity : null;
                tonnageInput.updateFullTc = false;
                tonnageInput.fullTc = response.data.fullTcpreview;

                Utility.Alert.Info("Last updated by " + response.data.userDisplayName + " " + formatUpdatedDate(response.data.updatedDate));

                setContactForContactSelector(response.data);

                dispatch(setTonnageInput(tonnageInput));

                dispatch(showLoading(false));

            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    };

    const formatUpdatedDate = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const setContactForContactSelector = (data: TonnageModel) => {
        let searchResults = Utility.CloneObject(contactSelectorData.searchResults) as ContactNameModel[];

        searchResults.push({
            id: data.operator,
            companyName: data.operatorName
        } as ContactNameModel);
        searchResults.push({
            id: data.headOwner,
            companyName: data.headOwnerName
        } as ContactNameModel);

        searchResults = _.uniqBy(searchResults, (item: ContactNameModel) => {
            return item.id;
        });
        dispatch(setContactSearchResults(searchResults));
    }

    const saveTonnage = (triggerM1: boolean, updateModifiedDate: boolean = true) => {
        try {
            validateTonnage();

            let isUpdateFullTc = tonnageInputViewData.tonnageInput.id.length === 0 ?
                true :
                tonnageInputViewData.tonnageInput.updateFullTc;

            let param = {
                id: tonnageInputViewData.tonnageInput.id,
                vesselName: tonnageInputViewData.tonnageInput.vesselName.trim(),
                portOpen: tonnageInputViewData.tonnageInput.portOpen,
                dateOpenStart: tonnageInputViewData.tonnageInput.dateOpenStart,
                dateOpenEnd: tonnageInputViewData.tonnageInput.dateOpenEnd,
                dwt: tonnageInputViewData.tonnageInput.dwt,
                yearBuilt: tonnageInputViewData.tonnageInput.yearBuilt,
                class: tonnageInputViewData.tonnageInput.class,
                deck: tonnageInputViewData.tonnageInput.deck,
                loa: tonnageInputViewData.tonnageInput.loa,
                beam: tonnageInputViewData.tonnageInput.beam,
                draft: tonnageInputViewData.tonnageInput.draft,
                gear: tonnageInputViewData.tonnageInput.gear,
                grabs: tonnageInputViewData.tonnageInput.grabs,
                gearless: tonnageInputViewData.tonnageInput.gearless,
                operator: tonnageInputViewData.tonnageInput.operator,
                headOwner: tonnageInputViewData.tonnageInput.headOwner,
                remarks: tonnageInputViewData.tonnageInput.remarks,
                capacity: tonnageInputViewData.tonnageInput.capacity,
                fullTC: isUpdateFullTc ? tonnageInputViewData.tonnageInput.fullTc : "",
                updateFullTc: isUpdateFullTc,
                updateModifiedDate: updateModifiedDate
            } as SaveTonnageParam;

            dispatch(showLoading(true));

            TonnageApi.Save(param)
                .then((response: BaseResponse<TonnageModel>) => {
                    if (response.success) {
                        if (param.id.length === 0) {
                            Utility.Alert.Success("Tonnage added successful");

                            dispatch(addTonnageName({
                                id: response.data.id,
                                dwt: response.data.dwt,
                                vesselName: response.data.vesselName
                            } as TonnageNameModel));

                        } else {
                            Utility.Alert.Success("Tonnage updated successful");
                        }

                        if (triggerM1) {
                            dispatch(setTriggerMatchingAreaModel({
                                triggerMatchingArea: new Date().getTime(),
                                matchingType: MatchingType.MatchingArea1,
                                model: response.data
                            } as TriggerMatchingAreaModel<TonnageModel>))
                        }

                        dispatch(showLoading(false));

                        refreshInput();
                    } else {
                        if (response.message === "Need Confirm") {
                            Swal.fire({
                                title: 'The order you are trying to input may be existed. Do you wish to add anyway?',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#3085d6',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'Yes',
                                cancelButtonText: 'No'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    TonnageApi.Save(param)
                                        .then((response: BaseResponse<TonnageModel>) => {
                                            if (response.success) {
                                                if (param.id.length === 0) {
                                                    Utility.Alert.Success("Tonnage added successful");

                                                    dispatch(addTonnageName({
                                                        id: response.data.id,
                                                        dwt: response.data.dwt,
                                                        vesselName: response.data.vesselName
                                                    } as TonnageNameModel));

                                                } else {
                                                    Utility.Alert.Success("Tonnage updated successful");
                                                }

                                                if (triggerM1) {
                                                    dispatch(setTriggerMatchingAreaModel({
                                                        triggerMatchingArea: new Date().getTime(),
                                                        matchingType: MatchingType.MatchingArea1,
                                                        model: response.data
                                                    } as TriggerMatchingAreaModel<TonnageModel>))
                                                }

                                                dispatch(showLoading(false));

                                                refreshInput();
                                            }
                                        });
                                }
                            })
                        } else {
                            dispatch(showLoading(false));
                            Utility.Alert.Error(response.message);
                        }
                    }
                })
                .catch((error) => {
                    dispatch(showLoading(false));
                    Utility.Alert.Error(error);
                });

            dispatch(setTonnageHistories([]));

        } catch (error) {
            dispatch(showLoading(false));
            Utility.Alert.Error(error);
        }
    }

    const validateTonnage = () => {
        if (tonnageInputViewData.tonnageInput.vesselName.length === 0) {
            throw "Vessel Name is required";
        }

        if (tonnageInputViewData.tonnageInput.portOpen === 0) {
            throw "Port open is required";
        }

        if (tonnageInputViewData.tonnageInput.dateOpenStart.length === 0 ||
            !Utility.DateFormat.isValidDate(tonnageInputViewData.tonnageInput.dateOpenStart)) {
            throw "Lay is required";
        }

        if (tonnageInputViewData.tonnageInput.dateOpenEnd.length === 0 ||
            !Utility.DateFormat.isValidDate(tonnageInputViewData.tonnageInput.dateOpenEnd)) {
            throw "Can is required";
        }

        if (!tonnageInputViewData.tonnageInput.dwt || tonnageInputViewData.tonnageInput.dwt === 0) {
            throw "Dwt is required";
        }

        if (new Date(tonnageInputViewData.tonnageInput.dateOpenStart).getTime() > new Date(tonnageInputViewData.tonnageInput.dateOpenEnd).getTime()) {
            throw "Lay must before Can";
        }
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    const parseFullTc = () => {
        if (!tonnageInputViewData.tonnageInput.fullTc || tonnageInputViewData.tonnageInput.fullTc.length == 0) {
            return;
        }

        dispatch(showLoading(true));

        TonnageApi.ParseFullTc({
            fullTc: tonnageInputViewData.tonnageInput.fullTc
        }).then((response) => {
            if (response.success) {
                if (response.data) {
                    if (response.data.vesselName) {
                        dispatch(setVesselName(response.data.vesselName));
                    }

                    if (response.data.dwt) {
                        dispatch(setDwt(response.data.dwt));
                    }

                    if (response.data.yearBuilt) {
                        dispatch(setYearBuilt(response.data.yearBuilt));
                    }

                    if (response.data.gear) {
                        dispatch(setGear(response.data.gear));
                    }

                    if (response.data.draft) {
                        dispatch(setDraft(response.data.draft));
                    }

                    if (response.data.loa) {
                        dispatch(setLoa(response.data.loa));
                    }

                    if (response.data.class) {
                        dispatch(setClass(response.data.class));
                    }

                    if (response.data.beam) {
                        dispatch(setBeam(response.data.beam));
                    }

                    if (response.data.deck) {
                        dispatch(setDeck(response.data.deck));
                    }

                    if (response.data.grabs) {
                        dispatch(setGrabs(response.data.grabs));
                    }

                    if (response.data.gearless) {
                        dispatch(setGearless(response.data.gearless));
                    }

                    if (response.data.capacity) {
                        dispatch(setCapacity(response.data.capacity));
                    }

                    Utility.Alert.Success("Parsed successfully");
                }
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
        }).finally(() => {
            dispatch(showLoading(false));
        });
    }

    return (
        <>
            <TonnageOperatorHistoryModal></TonnageOperatorHistoryModal>
            <CustomAccordion
                title="Quick Input"
                defaultOpen={true}
                className="top-quick-input"
                open={tonnageInputViewData.tonnageQuickInputExpand}
                onOpenChange={(isOpen: boolean) => {
                    dispatch(setTonnageQuickInputExpand(isOpen));
                }}
            >
                <Grid container>
                    <Grid item lg={2} xs={12} className="quick-input-component">
                        <Grid container>
                            <Grid item lg={9} xs={10}>
                                <TonnageNameSelector
                                    triggerFocusInput={tonnageInputViewData.triggerFocusTonnageNameInput}
                                    selectedTonnageId={tonnageInputViewData.selectedTonnageId}
                                    currentVesselName={tonnageInputViewData.tonnageInput.vesselName}
                                    onTonnageIdChange={(value: string) => {
                                        dispatch(setSelectedTonnageId(value));
                                    }}
                                    onVesselNameChange={(value: string) => {
                                        dispatch(setVesselName(value));
                                    }}
                                >
                                </TonnageNameSelector>
                            </Grid>
                            <Grid item lg={3} xs={2}>
                                <Tooltip title="Reset (Alt + R)">
                                    <Button
                                        className="clear-input-button"
                                        tabIndex={-1}
                                        color="success"
                                        variant="contained"
                                        onClick={() => {
                                            refreshInput();
                                        }}
                                    >
                                        <i className="fa fa-refresh" aria-hidden="true"></i>
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item lg={4} xs={12} className="quick-input-component">
                        <PortSelector
                            onPortChange={(value: PortModel | null) => {
                                if (value) {
                                    dispatch(setPortOpen((value as PortModel).id));
                                } else {
                                    dispatch(setPortOpen(0))
                                }
                            }}
                            portLabel='Port Open'
                            areaLabel='Area'
                            isAreaDisabled={true}
                            portValue={getPortById(tonnageInputViewData.tonnageInput.portOpen)}
                            isAreaRelatedToPort={true}
                        >
                        </PortSelector>
                    </Grid>
                    <Grid item lg={4} xs={12} className="quick-input-component">
                        <StartEndDatepicker
                            startDateLabel="Lay"
                            endDateLabel="Can"
                            useQuickEndDateInput={true}
                            onStartDateChange={(date: string) => {
                                if (date) {
                                    dispatch(setDateOpenStart(date));
                                }
                            }}
                            onEndDateChange={(date: string) => {
                                if (date) {
                                    dispatch(setDateOpenEnd(date));
                                }
                            }}
                            startDateValue={tonnageInputViewData.tonnageInput.dateOpenStart.toUpperCase()}
                            endDateValue={tonnageInputViewData.tonnageInput.dateOpenEnd.toUpperCase()}
                        >
                        </StartEndDatepicker>
                    </Grid>
                    <Grid item lg={2} xs={12}>
                        <Grid container>
                            <Grid item lg={6}>
                                <Tooltip title="Alt + S">
                                    <Button
                                        onClick={() => {
                                            saveTonnage(false);
                                        }}
                                        variant="outlined"
                                    >
                                        Save (s)
                                    </Button>
                                </Tooltip>
                            </Grid>
                            <Grid item lg={6}>
                                <Button
                                    onClick={() => {
                                        saveTonnage(true);
                                    }}
                                    variant="outlined"
                                >
                                    Save And M1
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CustomAccordion>
            <CustomAccordion
                title="Advanced Input"
                defaultOpen={true}
                className="top-advance-input"
                open={tonnageInputViewData.tonnageAdvanceInputExpand}
                onOpenChange={(isOpen: boolean) => {
                    dispatch(setTonnageAdvanceInputExpand(isOpen));
                }}
            >
                <Grid container>
                    <Grid item lg={3} xs={12}>
                        <TextField
                            fullWidth
                            label="DWT(T)"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={tonnageInputViewData.tonnageInput.dwt ? tonnageInputViewData.tonnageInput.dwt : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setDwt(value));
                            }}
                        >
                        </TextField>
                        <TextField
                            fullWidth
                            label="Year Built"
                            type='number'
                            value={tonnageInputViewData.tonnageInput.yearBuilt ? tonnageInputViewData.tonnageInput.yearBuilt : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setYearBuilt(value));
                            }}
                        >
                        </TextField>
                        <Autocomplete
                            options={["IACS", "NON-IACS"]}
                            renderInput={(params) => <TextField {...params} label="Class" />}
                            value={tonnageInputViewData.tonnageInput.class}
                            onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                dispatch(setClass(value ? value : ""));
                            }}
                        />
                        <Autocomplete
                            options={["Single", "Tween", "Flush Tween"]}
                            renderInput={(params) => <TextField {...params} label="Deck" />}
                            value={tonnageInputViewData.tonnageInput.deck}
                            onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                dispatch(setDeck(value ? value : ""));
                            }}
                        />
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <TextField
                            fullWidth
                            label="Loa"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={tonnageInputViewData.tonnageInput.loa ? tonnageInputViewData.tonnageInput.loa : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setLoa(value));
                            }}
                        >
                        </TextField>
                        <TextField
                            fullWidth
                            label="Beam"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={tonnageInputViewData.tonnageInput.beam ? tonnageInputViewData.tonnageInput.beam : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setBeam(value));
                            }}
                        ></TextField>
                        <TextField
                            fullWidth
                            label="Draft"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={tonnageInputViewData.tonnageInput.draft ? tonnageInputViewData.tonnageInput.draft : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setDraft(value));
                            }}
                        >
                        </TextField>
                        <TextField
                            fullWidth
                            label="Gear"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={tonnageInputViewData.tonnageInput.gear ? tonnageInputViewData.tonnageInput.gear : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setGear(value));
                            }}
                        >
                        </TextField>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <ContactSelector
                            label='Head Owners'
                            selectedContactId={tonnageInputViewData.tonnageInput.headOwner}
                            onContactIdChange={(value: string) => {
                                dispatch(setHeadOwner(value));

                                if (!tonnageInputViewData.tonnageInput.operator || tonnageInputViewData.tonnageInput.operator.length > 0) {
                                    dispatch(setOperator(value));
                                }
                            }}
                        >
                        </ContactSelector>
                        <div>
                            <Grid container>
                                <Grid item xs={10}>
                                    <ContactSelector
                                        label='Operator'
                                        selectedContactId={tonnageInputViewData.tonnageInput.operator}
                                        onContactIdChange={(value: string) => {
                                            if (!value || value.length === 0) {
                                                if (tonnageInputViewData.tonnageInput.headOwner && tonnageInputViewData.tonnageInput.headOwner.length > 0) {
                                                    dispatch(setOperator(tonnageInputViewData.tonnageInput.headOwner));
                                                }
                                            } else {
                                                dispatch(setOperator(value));
                                            }
                                        }}
                                    >
                                    </ContactSelector>
                                </Grid>
                                <Grid item xs={2}>
                                    <Tooltip title="Operator History">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            disabled={!tonnageInputViewData.tonnageInput.id || tonnageInputViewData.tonnageInput.id.length == 0}
                                            onClick={() => {
                                                dispatch(setTonnageId(tonnageInputViewData.tonnageInput.id));
                                                dispatch(setModalOpen(true));
                                            }}
                                        >
                                            <i className="fa fa-history" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </div>
                        <TextField
                            fullWidth
                            label="Remarks"
                            value={tonnageInputViewData.tonnageInput.remarks}
                            onChange={(event) => {
                                dispatch(setRemarks(event.target.value));
                            }}
                        >
                        </TextField>
                        <TextField
                            fullWidth
                            label="Capacity"
                            InputProps={{
                                inputComponent: NumberFormatCustom as any,
                            }}
                            value={tonnageInputViewData.tonnageInput.capacity ? tonnageInputViewData.tonnageInput.capacity : ""}
                            onChange={(event) => {
                                let value = event.target.value.length == 0 ? null : parseFloat(event.target.value);
                                dispatch(setCapacity(value));
                            }}
                        >
                        </TextField>
                    </Grid>
                    <Grid item lg={3} xs={12}>
                        <Grid container>
                            <Grid item xs={3}>
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={tonnageInputViewData.tonnageInput.grabs}
                                        onChange={(event) => {
                                            dispatch(setGrabs(event.currentTarget.checked));
                                        }}
                                    />}
                                    className="tonnage-input-grabs"
                                    label="Grabs"
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={tonnageInputViewData.tonnageInput.gearless}
                                        onChange={(event) => {
                                            dispatch(setGearless(event.currentTarget.checked));
                                        }}
                                    />}
                                    className="tonnage-input-grabs"
                                    label="Gearless"
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    onClick={() => {
                                        saveTonnage(false, false);
                                    }}
                                    variant="outlined"
                                >
                                    Update
                                </Button>
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    onClick={() => {
                                        parseFullTc();
                                    }}
                                    variant="outlined"
                                >
                                    Parse FullTC
                                </Button>
                            </Grid>
                        </Grid>
                        <TextField
                            label="Full Tc"
                            multiline
                            fullWidth
                            rows={6}
                            value={tonnageInputViewData.tonnageInput.fullTc}
                            onChange={(event) => {
                                dispatch(setFullTc(event.target.value));
                                dispatch(setUpdateFullTc(true));
                            }}
                            className="tonnage-input-full-tc"
                        />
                    </Grid>
                </Grid>
            </CustomAccordion>
        </>
    )
};

export default TonnageInput;